import { ReactRenderer } from '@tiptap/react';
import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import type { Instance as TippyInstance, Props } from 'tippy.js';
import tippy from 'tippy.js';
import { MENTION_SUGGESTION_CLASS } from '../const';
import type { MentionItem } from '../hooks/types';
import { searchPages } from '../hooks/use-search';
import { MentionList } from './mention-list';

interface SuggestionConfig {
  spaceId: string;
}

interface SuggestionItem {
  items: ({ query }: { query: string }) => Promise<MentionItem[]>;
  render: () => {
    onStart: (props: SuggestionProps) => void;
    onUpdate: (props: SuggestionProps) => void;
    onKeyDown: (props: SuggestionKeyDownProps) => boolean;
    onExit: () => void;
  };
  allowSpaces: boolean;
}

export const createSuggestion = ({ spaceId }: SuggestionConfig): SuggestionItem => ({
  allowSpaces: true,
  items: async ({ query }: { query: string }): Promise<MentionItem[]> => {
    const items = await searchPages({
      query,
      spaceId,
    });
    return items;
  },
  render: () => {
    let component: ReactRenderer<any, any>;
    let popup: TippyInstance<Props>;

    const cleanup = () => {
      try {
        if (popup) {
          popup.destroy();
        }
        if (component) {
          component.destroy();
        }
      } catch {
        //
      }
    };

    return {
      onStart: (props: SuggestionProps) => {
        cleanup();

        if (!props?.clientRect?.()) return;

        component = new ReactRenderer(MentionList, {
          props: {
            ...props,
            query: props.query,
            onHeightChange: () => {
              if (popup?.popperInstance) {
                void popup.popperInstance.update();
              }
            },
          },
          editor: props.editor,
        });

        const element = document.body;
        if (!element) return;

        popup = tippy(element, {
          getReferenceClientRect: () => props?.clientRect?.() || new DOMRect(),
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          theme: 'none',
          arrow: false,
          onCreate: ({ popper }) => {
            popper.querySelector('.tippy-box')?.classList.add(MENTION_SUGGESTION_CLASS);
          },
        });
      },

      onUpdate(props: SuggestionProps) {
        component.updateProps({
          ...props,
          query: props.query,
        });

        if (!props.clientRect) {
          return;
        }

        popup.setProps({
          getReferenceClientRect: () => props.clientRect?.() || new DOMRect(),
        });

        // Force update the popup position to handle content height changes
        if (popup.popperInstance) {
          void popup.popperInstance.update();
        }
      },

      onKeyDown(props: SuggestionKeyDownProps) {
        if (props.event.key === 'Escape') {
          popup.hide();
          return true;
        }

        return component.ref?.onKeyDown(props);
      },

      onExit() {
        cleanup();
      },
    };
  },
});
